import { getAuthHeaders } from '../../../actions/authActions';
import tempMeeApi from '../../../apis/tempMee';

/**
 * One page of the reviews a given office has received, from organization-service.
 *
 * Only office profile pages come through here. Professional pages stay on the
 * monolith's `admin/users/{id}/reviews`, which returns reviews in both
 * directions; this endpoint deliberately carries only professional→office ones,
 * so pointing a professional page at it would empty the table.
 *
 * The route is prefixed `/organization-service-api`, as the ticket's Dev Notes
 * named it, and is prefixed inline the way the auth-service clients prefix
 * theirs.
 *
 * `officeRegistermodelId` is the filter param, confirmed against the endpoint —
 * the same name the response row uses. It is not optional in practice: an
 * ignored filter lists every office's reviews rather than erroring, which on an
 * office page reads as someone else's data.
 *
 * The token goes in `Authorization` unwrapped, as `getAuthHeaders` returns it
 * and as every monolith caller sends it. If this 401s or 403s against a live
 * dev token, organization-service wants the `Bearer` prefix that the two
 * auth-service clients add — that is the only thing to try.
 *
 * @param {Object} params
 * @param {string} params.officeId - The office whose reviews to list
 * @param {number} params.page - 1-based page index
 * @param {number} [params.limit] - Page size; matches `LIMIT_VALUE` in `UserReview.js`
 * @returns {Promise<import('../types/OfficeReview.api').IGetOfficeReviewsApiResponse>}
 */
export const getOfficeReviews = async ({ officeId, page, limit = 15 }) => {
  const headers = await getAuthHeaders();

  const response = await tempMeeApi.get('/organization-service-api/admin/office-reviews', {
    params: { officeRegistermodelId: officeId, page, limit },
    headers,
  });

  return response.data;
};
